// components/EventManagement/ManageEventAttendees.js

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEventContext } from '../../context/EventContext';
import { useEventActions } from '../../hooks/useEventActions';
import AttendeesSummary from '../../components/AttendeesSummary';
import AttendeesList from '../AttendeesList';
import RefundActionModal from '../Modal/RefundActionModal';
import CancelByOrganizerModal from '../Modal/CancelByOrganizerModal';
import RejectRegistrationModal from '../Modal/RejectRegistrationModal';
import axiosClient from '../../api/axiosClient';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { isEventPast } from '../../utils/dateUtils';

const ManageEventAttendees = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { eventDetails, updateEventDetails } = useEventContext();
  const { capacity, attendees, needsVehicle } = eventDetails || { capacity: 0, attendees: [], needsVehicle: false };
  const requestedAttendeeId = searchParams.get('attendeeId');
  const currentEventId = eventDetails?.id || eventDetails?._id;
  const isCurrentEventLoaded = currentEventId && String(currentEventId) === String(id);

  // Actualizar los datos del evento cuando se monta el componente
  useEffect(() => {
    const updateEventData = async () => {
      try {
        const response = await axiosClient.get(`/api/events/manage/${id}`);
        if (response.data.success) {
          updateEventDetails(response.data.event);
        }
      } catch (error) {
        console.error('Error updating event data:', error);
      }
    };

    if (id) {
      updateEventData();
    }
  }, [id, updateEventDetails]);

  useEffect(() => {
    if (!requestedAttendeeId || !isCurrentEventLoaded) {
      return;
    }

    const attendeeExists = attendees.some(attendee => attendee._id === requestedAttendeeId);

    if (attendeeExists) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('attendeeId');
    setSearchParams(nextSearchParams, { replace: true });
    toast.error('No hemos encontrado la inscripción solicitada para este evento.');
  }, [attendees, isCurrentEventLoaded, requestedAttendeeId, searchParams, setSearchParams]);

  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [showRefundActionModal, setShowRefundActionModal] = useState(false);
  const [showCancelByOrganizerModal, setShowCancelByOrganizerModal] = useState(false);
  const [showRejectRegistrationModal, setShowRejectRegistrationModal] = useState(false);
  const [refundAction, setRefundAction] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleAttendeeDrawerOpen = (attendeeId) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('attendeeId', attendeeId);
    setSearchParams(nextSearchParams);
  };

  const handleAttendeeDrawerClose = () => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('attendeeId');
    setSearchParams(nextSearchParams);
  };

  const {
    handleApprove: originalHandleApprove,
    handleReject: originalHandleReject,
    handleCancelByOrganizer: originalHandleCancelByOrganizer,
    handleDenyCancellation: originalHandleDenyCancellation,
    handleAcceptCancellation: originalHandleAcceptCancellation,
    handleMarkRefunded: originalHandleMarkRefunded
  } = useEventActions(eventDetails?.id);

  // Wrapper functions with past event validation
  const handleApprove = (attendeeId) => {
    if (isEventPast(eventDetails?.endDate)) {
      toast.error('No puedes aprobar asistentes de un evento que ya ha finalizado.');
      return;
    }
    return originalHandleApprove(attendeeId);
  };

  const handleReject = (attendeeId, organizerMessage) => {
    if (isEventPast(eventDetails?.endDate)) {
      toast.error('No puedes rechazar asistentes de un evento que ya ha finalizado.');
      return;
    }
    return originalHandleReject(attendeeId, organizerMessage);
  };

  const handleCancelByOrganizer = (attendeeId, refundAmount, organizerMessage) => {
    if (isEventPast(eventDetails?.endDate)) {
      toast.error('No puedes cancelar asistentes de un evento que ya ha finalizado.');
      return;
    }
    return originalHandleCancelByOrganizer(attendeeId, refundAmount, organizerMessage);
  };

  const handleDenyCancellation = (attendeeId, organizerMessage) => {
    if (isEventPast(eventDetails?.endDate)) {
      toast.error('No puedes denegar cancelaciones de un evento que ya ha finalizado.');
      return;
    }
    return originalHandleDenyCancellation(attendeeId, organizerMessage);
  };

  const handleAcceptCancellation = (attendeeId, refundAmountCents, organizerMessage) => {
    if (isEventPast(eventDetails?.endDate)) {
      toast.error('No puedes aceptar cancelaciones de un evento que ya ha finalizado.');
      return;
    }
    return originalHandleAcceptCancellation(attendeeId, refundAmountCents, organizerMessage);
  };

  const handleMarkRefunded = (attendeeId) => {
    if (isEventPast(eventDetails?.endDate)) {
      toast.error('No puedes marcar reembolsos de un evento que ya ha finalizado.');
      return;
    }
    return originalHandleMarkRefunded(attendeeId);
  };

  const handleRejectClick = (attendeeId) => {
    if (isEventPast(eventDetails?.endDate)) {
      toast.error('No puedes rechazar asistentes de un evento que ya ha finalizado.');
      return;
    }
    
    // Buscar el asistente seleccionado para determinar si tiene un ticket pagado
    const attendee = attendees.find(att => att._id === attendeeId);
    setSelectedAttendee(attendee);

    // Determinar el tipo de acción basado en el estado del asistente
    if (attendee.status === 'attending') {
      setShowCancelByOrganizerModal(true);
    } else {
      // Para attendees en estado 'pending' o similar, usar el modal de rechazo
      setShowRejectRegistrationModal(true);
    }
  };

  // Función para manejar el rechazo directo desde el drawer (con mensaje)
  const handleRejectWithMessage = async (attendeeId, organizerMessage) => {
    try {
      setIsLoading(true);
      await handleReject(attendeeId, organizerMessage);
    } catch (error) {
      console.error('Error en handleRejectWithMessage:', error);
      throw error; // Re-lanzar el error para que el drawer lo maneje
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefundAction = (action) => {
    setRefundAction(action);
    setShowRefundActionModal(true);
  };

  const confirmRefundAction = async ({
    organizerMessage,
    refundAmountCents = null
  }) => {
    if (!selectedAttendee) return;

    setIsLoading(true);
    try {
      if (refundAction === 'accept') {
        await handleAcceptCancellation(selectedAttendee._id, refundAmountCents, organizerMessage);
      } else if (refundAction === 'deny') {
        await handleDenyCancellation(selectedAttendee._id, organizerMessage);
      }
    } finally {
      setIsLoading(false);
      setShowRefundActionModal(false);
      setSelectedAttendee(null);
      setRefundAction('');
    }
  };

  const confirmCancelByOrganizer = async (attendeeId, refundAmount, message) => {
    // Si se llama desde el modal interno, usar selectedAttendee
    const targetAttendeeId = attendeeId || selectedAttendee?._id;
    if (!targetAttendeeId) return;

    setIsLoading(true);
    try {
      await handleCancelByOrganizer(targetAttendeeId, refundAmount, message);
    } finally {
      setIsLoading(false);
      setShowCancelByOrganizerModal(false);
      setSelectedAttendee(null);
    }
  };

  const handleDenyCancellationClick = (attendeeId, organizerMessage) => {
    // Si no se proporciona organizerMessage, abrir el modal para obtenerlo
    if (!organizerMessage) {
      const attendee = attendees.find(att => att._id === attendeeId);
      setSelectedAttendee(attendee);
      handleRefundAction('deny');
      return;
    }
    return handleDenyCancellation(attendeeId, organizerMessage);
  };

  const handleAcceptCancellationClick = (attendeeId, refundAmountCents, organizerMessage) => {
    // Si no se proporciona organizerMessage, abrir el modal para obtenerlo
    if (!organizerMessage) {
      const attendee = attendees.find(att => att._id === attendeeId);
      setSelectedAttendee(attendee);
      handleRefundAction('accept');
      return;
    }
    return handleAcceptCancellation(attendeeId, refundAmountCents, organizerMessage);
  };

  const handleMarkRefundedClick = (attendeeId) => {
    handleMarkRefunded(attendeeId);
  };

  const confirmRejectRegistration = async (attendeeId, organizerMessage) => {
    if (!attendeeId) {
      return;
    }

    setIsLoading(true);
    try {
      await handleReject(attendeeId, organizerMessage);
      // Solo cerrar el modal si la operación fue exitosa
      setShowRejectRegistrationModal(false);
      setSelectedAttendee(null);
    } catch (error) {
      console.error('Error in handleReject:', error);
      // No cerrar el modal si hay error, para que el usuario pueda intentar de nuevo
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AttendeesSection>
      <Container>
        <AttendeesSummary attendees={attendees} capacity={capacity} />
        <AttendeesList
          attendees={attendees}
          onApprove={handleApprove}
          onReject={handleRejectClick}
          onRejectWithMessage={handleRejectWithMessage}
          onDenyCancellation={handleDenyCancellationClick}
          onAcceptCancellation={handleAcceptCancellationClick}
          onMarkRefunded={handleMarkRefundedClick}
          onCancelByOrganizer={confirmCancelByOrganizer}
          needsVehicle={needsVehicle}
          deepLinkedAttendeeId={requestedAttendeeId}
          onAttendeeOpen={handleAttendeeDrawerOpen}
          onAttendeeDrawerClose={handleAttendeeDrawerClose}
        />

        <RefundActionModal
          isOpen={showRefundActionModal}
          onClose={() => {
            setShowRefundActionModal(false);
            setSelectedAttendee(null);
            setRefundAction('');
          }}
          onConfirm={confirmRefundAction}
          actionType={refundAction}
          attendee={selectedAttendee}
          isLoading={isLoading}
        />

        <CancelByOrganizerModal
          isOpen={showCancelByOrganizerModal}
          onClose={() => {
            setShowCancelByOrganizerModal(false);
            setSelectedAttendee(null);
          }}
          onConfirm={confirmCancelByOrganizer}
          attendee={selectedAttendee}
          isLoading={isLoading}
        />

        <RejectRegistrationModal
          isOpen={showRejectRegistrationModal}
          onClose={() => {
            setShowRejectRegistrationModal(false);
            setSelectedAttendee(null);
          }}
          onConfirm={confirmRejectRegistration}
          attendee={selectedAttendee}
          isLoading={isLoading}
        />
      </Container>
    </AttendeesSection>
  );
};

export default ManageEventAttendees;

const AttendeesSection = styled.section`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  max-width: 1400px;
  padding: 0 ${({ theme }) => theme.sizing.md};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;
